"use client";

import { useAtomValue } from "jotai";
import { useEffect } from "react";
import { useTourKitContext } from "./context";
import { tourCompletedAtom } from "./storage";
import type { TourStepDefinition } from "./types";

interface TourProps {
  autoStart?: boolean;
  id: string;
  persist?: boolean;
  steps: TourStepDefinition[];
}

const IS_DEV =
  typeof process !== "undefined" && process.env?.NODE_ENV !== "production";

export function Tour({
  id,
  autoStart = false,
  persist = true,
  steps,
}: TourProps) {
  const { registerTour, unregisterTour, start, activeTour } =
    useTourKitContext();
  const completed = useAtomValue(tourCompletedAtom(id));

  useEffect(() => {
    if (steps.length === 0) {
      throw new Error(`Tour "${id}" must declare at least one step`);
    }
    registerTour({ id, steps, persist });
    if (IS_DEV) {
      console.info("[onboarding-tour] registered", id, {
        steps: steps.length,
      });
    }
    return () => unregisterTour(id);
  }, [id, steps, persist, registerTour, unregisterTour]);

  useEffect(() => {
    if (!autoStart) {
      if (IS_DEV) {
        console.info("[onboarding-tour] skip", id, "reason=autoStart=false");
      }
      return;
    }
    if (activeTour) {
      if (IS_DEV) {
        console.info(
          "[onboarding-tour] skip",
          id,
          `reason=another-tour-active(${activeTour.id})`
        );
      }
      return;
    }
    if (persist && completed) {
      if (IS_DEV) {
        console.info(
          "[onboarding-tour] skip",
          id,
          "reason=already-completed (clear localStorage to retest)"
        );
      }
      return;
    }
    if (IS_DEV) {
      console.info("[onboarding-tour] start", id);
    }
    start(id);
  }, [autoStart, activeTour, persist, completed, id, start]);

  if (IS_DEV) {
    return <span data-tour-debug={id} hidden />;
  }
  return null;
}
